import {
  Cache,
  cacheExchange as createCacheExchange,
} from "@urql/exchange-graphcache";
import { relayPagination } from "@urql/exchange-graphcache/extras";
import { graphql, VariablesOf } from "gql.tada";
import { gql } from "urql";
import schema from "./schema.json";

const NotificationFragment = gql`
  fragment _ on Notification {
    id
    readAt
    archived
  }
`;

const BulkUpdateNotificationsByIdsMutation = graphql(`
  mutation BulkUpdateNotificationsByIds(
    $ids: [ID!]!
    $input: BulkUpdateNotificationsInput!
  ) {
    bulkUpdateNotificationsByIds(ids: $ids, input: $input)
  }
`);

export const cacheExchange = createCacheExchange({
  schema: schema,
  keys: {
    AdsAnalyticsDataDate: () => null,
    AdsAnalyticsExportData: () => null,
    CampaignPerformance: () => null,
    CampaignPerformanceDetailOrderBy: () => null,
    CampaignPerformanceMetrics: () => null,
    DashboardPageSelector: () => null,
    DashboardPageTransitionConfig: () => null,
    DashboardPageTransitionMetrics: () => null,
    DashboardPageTransitionPoint: () => null,
    AIArtifactPage: () => null,
    AIArtifactContent: () => null,
    DateToFloatValue: () => null,
    Device: () => null,
    Geography: () => null,
    IndexWorkerJobConnection: () => null,
    IndexWorkerJobEdge: () => null,
    IndexWorkerResult: () => null,
    IndexWorkerTask: () => null,
    IndexWorkerTaskResult: () => null,
    IntRange: () => null,
    Query: () => null,
    QueryGroupPattern: () => null,
    RegisteredSearchConsoleServiceAccountProperty: () => null,
    RegisteredSearchConsoleUserProperty: () => null,
    SearchAnalyticsAggregatedDataByQueryGroup: () => null,
    SearchAnalyticsAggregatedDataByURLGroup: () => null,
    SearchAnalyticsAggregatedDataNonGrouped: () => null,
    SearchAnalyticsBigQuery: (data) => (data.id ? String(data.id) : null),
    SearchAnalyticsDataDate: () => null,
    SearchAnalyticsExportData: () => null,
    SearchAnalyticsProject: (data) => (data.id ? String(data.id) : null),
    SearchAnalyticsOnboardingBootstrapJob: () => null,
    SearchAnalyticsOnboardingSnapshotTopPage: () => null,
    SearchAnalyticsOnboardingSnapshotTopQuery: () => null,
    SearchAnalyticsOnboardingSnapshotTopQueryPage: () => null,
    SearchAnalyticsOnboardingBusinessContext: () => null,
    SearchAnalyticsOnboardingGoalSelection: () => null,
    SearchAnalyticsOnboardingGSCProperty: () => null,
    SearchAnalyticsOnboardingSession: (data) =>
      typeof data.id === "string" ? data.id : null,
    SearchAnalyticsOnboardingSnapshot: () => null,
    SearchAnalyticsOnboardingSnapshotDailyRow: () => null,
    SearchAnalyticsOnboardingSnapshotDateRange: () => null,
    SearchAnalyticsOnboardingSnapshotWarning: () => null,
    SearchAnalyticsOnboardingSnapshotRowCounts: () => null,
    SearchAnalyticsRawComparingData: () => null,
    SearchAnalyticsRawData: () => null,
    SearchAnalyticsRawDataOrder: () => null,
    Session: (data) =>
      data.gaSessionId && data.userPseudoId
        ? `${data.gaSessionId}-${data.userPseudoId}`
        : null,
    SessionDetail: (data) =>
      data.gaSessionId && data.userPseudoId
        ? `${data.gaSessionId}-${data.userPseudoId}`
        : null,
    SessionEvent: () => null,
    SessionEventParam: () => null,
    SessionPerformance: () => null,
    SessionPerformanceComparingDetail: () => null,
    SessionPerformanceDetail: () => null,
    SessionPerformanceDetailOrderBy: () => null,
    TimeRange: () => null,
    TrafficSourceMedium: () => null,
    URLGroup: () => null,
    URLGroupPattern: () => null,
    UserAnalyticsChartDisplaySettings: () => null,
    UserAnalyticsChartGrouping: () => null,
    UserAnalyticsDataDate: () => null,
    UserAnalyticsDetailDisplayDefaults: () => null,
    UserAnalyticsDisplaySettings: () => null,
    UserAnalyticsEventParamBackfill: () => null,
    UserAnalyticsExportData: () => null,
    UserAnalyticsOnboardingApplyTargetItem: () => null,
    UserAnalyticsOnboardingApplyTargetState: () => null,
    UserAnalyticsOnboardingBigQuerySource: () => null,
    UserAnalyticsOnboardingBootstrapJob: () => null,
    UserAnalyticsOnboardingBusinessContext: () => null,
    UserAnalyticsOnboardingDataSource: () => null,
    UserAnalyticsOnboardingDateRange: () => null,
    UserAnalyticsOnboardingSession: () => null,
    UserAnalyticsOnboardingSnapshot: () => null,
    UserAnalyticsOnboardingSnapshotKeyEventCoverage: () => null,
    UserAnalyticsOnboardingSnapshotPageAggregate: () => null,
    UserAnalyticsOnboardingSnapshotTotals: () => null,
    UserAnalyticsOnboardingSnapshotTrafficAggregate: () => null,
    UserAnalyticsOnboardingSnapshotWarning: () => null,
    UserAnalyticsProject: (data) => (data.id ? String(data.id) : null),
    UserAnalyticsViewPreset: (data) =>
      typeof data.presetID === "string" ? data.presetID : null,
    UserOrganization: () => null,
    WatchRuleConnection: () => null,
    WatchRuleEdge: () => null,
    RecommendedURLGroup: () => null,
    RecommendedQueryGroup: () => null,
    AgentChatEdge: () => null,
    AgentChatConnection: () => null,
    SystemMemoConnection: () => null,
    SystemMemoEdge: () => null,
    TextUIPart: () => null,
    ReasoningUIPart: () => null,
    ToolUIPart: () => null,
    SourceUrlUIPart: () => null,
    SourceDocumentUIPart: () => null,
    FileUIPart: () => null,
    StepStartUIPart: () => null,
    DataUIPart: () => null,
  },
  resolvers: {
    Query: {
      // AgentChat の接続は Relay 形式でページング
      agentChats: relayPagination(),
      // project ごとの WatchRule 一覧を cursor page 間で結合する
      watchRules: relayPagination(),
    },
    SearchAnalytics: {
      rawData: relayPagination(),
      rawComparingData: relayPagination(),
    },
  },
  updates: {
    Mutation: {
      bulkUpdateNotificationsByIds: (_result, args, cache) => {
        updateCacheForBulkNotificationsByIds(
          args as VariablesOf<typeof BulkUpdateNotificationsByIdsMutation>,
          cache,
        );
      },
      bulkUpdateNotificationsByFilter: (_result, _args, cache) => {
        cache.invalidate("NotificationConnection");
      },
      updateUserAnalytics: (_result, _args, cache) => {
        // UAのキーイベントが更新されたら、関連するキャッシュを無効化
        cache.invalidate("UserAnalyticsProject");
        cache.invalidate("UserAnalytics");
        cache.invalidate("UserAnalyticsView");
      },
      updateUserAnalyticsProjectLevelFilter: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      clearUserAnalyticsProjectLevelFilter: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      createUserAnalyticsCustomChannelGroup: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      updateUserAnalyticsCustomChannelGroup: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      deleteUserAnalyticsCustomChannelGroup: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      updateUserAnalyticsCustomChannelGroupOrder: (_result, _args, cache) => {
        invalidateUserAnalyticsEntities(cache);
      },
      deleteSearchAnalyticsViewCache: (_result, args, cache) => {
        cache.invalidate("SearchAnalytics");
      },
      deleteSearchAnalyticsView: (_result, _args, cache) => {
        invalidateDeletedMetricWatchTarget(cache, [
          "SearchAnalytics",
          "SearchAnalyticsProject",
          "SearchAnalyticsView",
        ]);
      },
      deleteSearchAnalyticsDefaultCache: (_result, args, cache) => {
        cache.invalidate("SearchAnalytics");
      },
      deleteSearchAnalyticsAllViewCache: (_result, args, cache) => {
        cache.invalidate("SearchAnalytics");
        cache.invalidate("SearchAnalyticsView");
      },
      deleteUserAnalyticsAllViewCache: (_result, _args, cache) => {
        cache.invalidate("UserAnalytics");
        cache.invalidate("UserAnalyticsView");
        cache.invalidate("UserAnalyticsProject");
      },
      deleteUserAnalyticsDefaultCache: (_result, _args, cache) => {
        cache.invalidate("UserAnalytics");
        cache.invalidate("UserAnalyticsProject");
      },
      deleteUserAnalyticsViewCache: (_result, _args, cache) => {
        cache.invalidate("UserAnalytics");
        cache.invalidate("UserAnalyticsView");
        cache.invalidate("UserAnalyticsProject");
      },
      deleteUserAnalyticsView: (_result, _args, cache) => {
        invalidateDeletedMetricWatchTarget(cache, [
          "UserAnalytics",
          "UserAnalyticsProject",
          "UserAnalyticsView",
        ]);
      },
      deleteProject: (_result, _args, cache) => {
        invalidateDeletedMetricWatchTarget(cache, [
          "SearchAnalytics",
          "SearchAnalyticsProject",
          "SearchAnalyticsView",
          "UserAnalytics",
          "UserAnalyticsProject",
          "UserAnalyticsView",
          "IndexWorker",
          "IndexWorkerProject",
          "Organization",
        ]);
      },
      deleteAgentChat: (_result, args, cache) => {
        const { id } = args;
        cache.invalidate(`AgentChat:${id}`);
      },
      createAgentChat: (_result, _args, cache) => {
        const fields = cache.inspectFields("Query");
        for (const field of fields) {
          if (field.fieldName === "agentChats") {
            cache.invalidate("Query", "agentChats", field.arguments);
          }
        }
      },
      createUserMemo: (_result, _args, cache) => {
        invalidateMemoQueries(cache);
      },
      updateUserMemo: (_result, _args, cache) => {
        invalidateMemoQueries(cache);
      },
      deleteUserMemo: (_result, _args, cache) => {
        invalidateMemoQueries(cache);
      },
      restoreUserMemo: (_result, _args, cache) => {
        invalidateMemoQueries(cache);
      },
      createSystemMemo: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      updateSystemMemo: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      publishSystemMemo: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      archiveSystemMemo: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      createSystemMemoLabel: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      updateSystemMemoLabel: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      archiveSystemMemoLabel: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      restoreSystemMemoLabel: (_result, _args, cache) => {
        invalidateSystemMemoQueries(cache);
      },
      startSearchAnalyticsOnboardingBootstrap: (_result, _args, cache) => {
        const fields = cache.inspectFields("Query");
        for (const field of fields) {
          if (field.fieldName === "searchAnalyticsOnboardingBootstrapJob") {
            cache.invalidate(
              "Query",
              "searchAnalyticsOnboardingBootstrapJob",
              field.arguments,
            );
          }
          if (field.fieldName === "latestSearchAnalyticsOnboardingSession") {
            cache.invalidate(
              "Query",
              "latestSearchAnalyticsOnboardingSession",
              field.arguments,
            );
          }
        }
      },
      createWatchRule: (_result, _args, cache) => {
        invalidateWatchRuleQueries(cache);
      },
      updateWatchRule: (_result, _args, cache) => {
        invalidateWatchRuleQueries(cache);
      },
      enableWatchRule: (_result, _args, cache) => {
        invalidateWatchRuleQueries(cache);
      },
      disableWatchRule: (_result, _args, cache) => {
        invalidateWatchRuleQueries(cache);
      },
      deleteWatchRule: (_result, _args, cache) => {
        invalidateWatchRuleQueries(cache);
      },
    },
  },
  optimistic: {
    bulkUpdateNotificationsByIds: (args, cache) => {
      updateCacheForBulkNotificationsByIds(
        args as VariablesOf<typeof BulkUpdateNotificationsByIdsMutation>,
        cache,
      );
      return { __typename: "Mutation", bulkUpdateNotificationsByIds: true };
    },
  },
});

const updateCacheForBulkNotificationsByIds = (
  variables: VariablesOf<typeof BulkUpdateNotificationsByIdsMutation>,
  cache: Cache,
) => {
  const { ids, input } = variables;
  const { read, archived } = input;

  const now = new Date().toISOString();

  ids.forEach((id) => {
    const data: { id: string; __typename: "Notification" } & Partial<{
      readAt: string | null;
      archived: boolean;
    }> = { id, __typename: "Notification" };

    if (read !== undefined && read !== null) {
      data.readAt = read ? now : null;
    }
    if (archived !== undefined && archived !== null) {
      data.archived = archived;
    }

    cache.writeFragment(NotificationFragment, data);
  });

  // NotificationConnectionを無効化して、UnreadNotificationCountQueryが再実行されるようにする
  cache.invalidate("Query", "viewer");
  // アーカイブ状態の変更を各タブのキャッシュへ反映させるため、通知接続のキャッシュも無効化する
  cache.invalidate("NotificationConnection");
};

/**
 * memo は context/filter args 付きの root query で読むため、
 * memo mutation 後は現在 cache にある全 memos query を再取得対象にします。
 */
const invalidateMemoQueries = (cache: Cache) => {
  for (const field of cache.inspectFields("Query")) {
    if (field.fieldName === "memos") {
      cache.invalidate("Query", "memos", field.arguments);
    }
  }
};

/**
 * system memo は管理一覧、label 一覧、通常 memo 表示の複数 root query に投影されるため、
 * mutation 後は cache 内に残っている関連 query をまとめて再取得対象にします。
 */
const invalidateSystemMemoQueries = (cache: Cache) => {
  for (const field of cache.inspectFields("Query")) {
    if (
      field.fieldName === "memos" ||
      field.fieldName === "systemMemos" ||
      field.fieldName === "systemMemoLabels"
    ) {
      cache.invalidate("Query", field.fieldName, field.arguments);
    }
  }
};

/**
 * UA の集計条件を変える mutation は、Graphcache の typename 単位で無効化し、
 * UserAnalytics 配下の active query を新しい条件で再取得させます。
 */
const invalidateUserAnalyticsEntities = (cache: Cache) => {
  cache.invalidate("UserAnalytics");
};

/**
 * WatchRule 一覧は projectID / pagination args 付きの root query のため、
 * mutation 後は cache にある watchRules query をまとめて再取得対象にします。
 */
const invalidateWatchRuleQueries = (cache: Cache) => {
  for (const field of cache.inspectFields("Query")) {
    if (field.fieldName === "watchRules") {
      cache.invalidate("Query", "watchRules", field.arguments);
    }
  }
};

/** Target cascade 後は target/source 投影と全 cursor page の WatchRule を同時に捨てます。 */
const invalidateDeletedMetricWatchTarget = (
  cache: Cache,
  entityTypes: readonly string[],
) => {
  for (const entityType of entityTypes) {
    cache.invalidate(entityType);
  }
  invalidateWatchRuleQueries(cache);
};
